import React from 'react';
import { Card, CardBody, CardHeader, Badge } from 'reactstrap';
import { Tool, FileText, File, Shield } from 'react-feather';

interface Expense {
    type: string;
    date: string;
    description: string;
    amount: number;
    vehicle_id?: number;
}

interface RecentExpensesProps {
    expenses: Expense[];
}

export default function RecentExpenses({ expenses }: RecentExpensesProps) {
    const getIcon = (type: string) => {
        const iconConfig = {
            lpo: { icon: FileText, color: '#3b82f6', bg: '#eff6ff', label: 'LPO' },
            maintenance: { icon: Tool, color: '#f59e0b', bg: '#fffbeb', label: 'Maintenance' },
            rc_renewal: { icon: File, color: '#8b5cf6', bg: '#f5f3ff', label: 'RC Renewal' },
            insurance_renewal: { icon: Shield, color: '#10b981', bg: '#f0fdf4', label: 'Insurance' },
            fuel: { icon: FileText, color: '#ef4444', bg: '#fef2f2', label: 'Fuel Expense' },
        };
        const config = iconConfig[type as keyof typeof iconConfig] || { icon: FileText, color: '#6b7280', bg: '#f3f4f6', label: 'Expense' };
        const Icon = config.icon;

        return (
            <div
                className={`activity-icon ${type}`}
                style={{ background: config.bg }}
            >
                <Icon size={20} color={config.color} strokeWidth={2.5} />
            </div>
        );
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    };

    const getTypeLabel = (type: string) => {
        const labels: { [key: string]: string } = {
            lpo: 'LPO',
            maintenance: 'Maintenance',
            rc_renewal: 'RC Renewal',
            insurance_renewal: 'Insurance',
            fuel: 'Fuel Expense',
        };
        return labels[type] || 'Expense';
    };

    const getTypeColor = (type: string) => {
        const colors: { [key: string]: string } = {
            lpo: 'primary',
            maintenance: 'warning',
            rc_renewal: 'info',
            insurance_renewal: 'success',
            fuel: 'danger',
        };
        return colors[type] || 'secondary';
    };

    return (
        <Card className="chart-card h-100">
            <CardHeader className="pb-0">
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <h5 className="mb-0">Recent Expenses</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            Latest financial transactions
                        </p>
                    </div>
                    {expenses.length > 0 && (
                        <span className="badge bg-light-danger text-danger">
                            {expenses.length} Expenses
                        </span>
                    )}
                </div>
            </CardHeader>
            <CardBody>
                {expenses.length === 0 ? (
                    <div className="empty-state py-4">
                        <div className="empty-state-icon">
                            <FileText size={32} className="text-muted" />
                        </div>
                        <h6 className="text-muted">No Recent Expenses</h6>
                        <p className="text-muted mb-0">Expense transactions will appear here</p>
                    </div>
                ) : (
                    <div className="activity-timeline">
                        {expenses.map((expense, index) => (
                            <div
                                key={index}
                                className="activity-item d-flex align-items-center mb-3 pb-3"
                                style={{
                                    borderBottom: index < expenses.length - 1 ? '1px solid #e2e8f0' : 'none',
                                    animationDelay: `${index * 0.05}s`
                                }}
                            >
                                <div className="me-3">
                                    {getIcon(expense.type)}
                                </div>
                                <div className="activity-content flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Badge color={`light-${getTypeColor(expense.type)}`} className={`text-${getTypeColor(expense.type)}`}>
                                            {getTypeLabel(expense.type)}
                                        </Badge>
                                        <div className="text-end">
                                            <span className="fw-bold text-dark" style={{ fontSize: '1rem' }}>
                                                {expense.amount.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem', lineHeight: '1.4' }}>
                                            {expense.description}
                                        </p>
                                        <small className="text-muted ms-2" style={{ whiteSpace: 'nowrap' }}>
                                            {formatDate(expense.date)}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardBody>
        </Card>
    );
}
